// Copyright ©️ 2026 eVolve MEP, LLC

import React, { Fragment } from 'react';

import { type TaskType, type TaskTypeGroup, useEvolvePaginatedGet } from '@evolvemep/api-ts';
import { Textarea, TextInput, Flex, Text, ActionIcon, Divider, Space, Stack } from '@mantine/core';
import type { UseFormReturnType } from '@mantine/form';

import { EvolveIcon } from 'assets/icons/EvolveIcon';
import { FileUploads } from 'components/Mantine/FileUploads/FileUploads';
import { ImageUpload } from 'components/Mantine/FileUploads/ImageUpload';
import { createdKey } from 'components/Mantine/TypeSafeSelect/Creatables/useCreatable';
import { SearchableMultiSelect } from 'components/Mantine/TypeSafeSelect/PaginatedSelectors/SearchableMultiSelect';
import { SearchableSelect } from 'components/Mantine/TypeSafeSelect/PaginatedSelectors/SearchableSelect';
import { isNil } from 'helpers/isNotNil';
import { validateUserNameOrEmail } from 'helpers/userHelpers';
import type { TaskTypeFormValues } from 'modules/Shop/Tasks/TaskTypes/types';

export const TaskTypeFormComponents = ({
  taskType,
  form,
}: {
  taskType?: TaskType;
  form: UseFormReturnType<TaskTypeFormValues>;
}) => {
  const paginatedGetTaskTypeGroups = useEvolvePaginatedGet('shop/taskTypeGroup');

  const paginatedCompanyMembers = useEvolvePaginatedGet('admin/user');

  return (
    <>
      <TextInput label="Task Name" autoFocus data-autofocus required maxLength={64} {...form.getInputProps('name')} />
      <SearchableSelect
        label="Task Group"
        clearSearchOnFocus
        paginatedGet={paginatedGetTaskTypeGroups}
        placeholder="Type to search or create..."
        searchKey="name"
        idKey="taskTypeGroupId"
        useLike={false}
        getItemLabel={(g: TaskTypeGroup) => g.name}
        clearable
        creatable
        createLabel="New task group"
        onCreate={(value) =>
          form.setFieldValue('taskTypeGroup', {
            taskTypeGroupId: createdKey,
            name: value,
          })
        }
        {...form.getInputProps('taskTypeGroup')}
      />
      <SearchableMultiSelect
        label="Default Assignee"
        paginatedGet={paginatedCompanyMembers}
        clearable
        getItemLabel={validateUserNameOrEmail}
        idKey="userId"
        {...form.getInputProps('defaultAssignees')}
      />
      <Textarea label="Description" maxLength={342} {...form.getInputProps('description')} />{' '}
      <ImageUpload label="Task Image" {...form.getInputProps('image')} existingFile={taskType?.image?.fileId} />
      {isNil(taskType) && (
        <FileUploads
          label="Default Attachments"
          description="These default attachments will be added to all new instances of this task."
          renderChildrenInUploadBox
          onAddFiles={async (files) =>
            form.setFieldValue('attachments', [
              ...(form.values.attachments ?? []),
              ...files.map((file) => ({
                type: 'file' as const,
                file,
              })),
            ])
          }
          onAddWebLink={async (webLink) =>
            form.setFieldValue('attachments', [
              ...(form.values.attachments ?? []),
              {
                type: 'webLink' as const,
                webLink,
              },
            ])
          }
          showUploadBox
        >
          <Stack gap={4}>
            {form.values.attachments?.map((a, i) => (
              <Fragment key={a.type === 'file' ? a.file.name : a.webLink.name}>
                <Flex align="center" justify="space-between">
                  <Flex align="center" gap={6}>
                    <EvolveIcon icon={a.type === 'file' ? 'Attachment' : 'External'} size="sm" />
                    <Text fz="sm">{a.type === 'file' ? a.file.name : a.webLink.name || a.webLink.url}</Text>
                  </Flex>
                  <ActionIcon
                    color="red"
                    onClick={(e) => {
                      form.removeListItem('attachments', i);
                      e.stopPropagation();
                    }}
                  >
                    <EvolveIcon icon="Delete" size="sm" />
                  </ActionIcon>
                </Flex>
                <Divider />
              </Fragment>
            ))}
          </Stack>
          {!!form.values.attachments?.length && <Space mb="xs" />}
        </FileUploads>
      )}
    </>
  );
};
